/* eslint-disable no-nested-ternary */
import {
  Autocomplete,
  Box,
  Checkbox,
  Divider,
  Grid,
  Group,
  NumberInput,
  Select,
  Stack,
  Text as MantineText,
  TextInput,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { ChangeEvent, ReactNode, useContext, useEffect, useState } from 'react';
import { IconAlignLeft, IconBorderRadius, IconCheck, IconLetterCase, IconPhoto, IconTextSize } from '@tabler/icons';
import { BookitupFormContext } from '../../../Service/Context/BookitupFormContext';
import MantineColorPicker from '../../../Atoms/inputs/MantineColorPicker';
import { WindowContext } from '../../../Service/Context/WindowContext';
import { DataS } from '../../../Service/DataS';
import GradientPicker from '../../../Atoms/inputs/GradientPicker';
import { ThemeText } from '../../../Atoms/ThemeText';

const getPixelValue = (value: string | null | undefined, fallback: number) => {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }

  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : fallback;
};

const GENERIC_FONT_FAMILIES = [
  'cursive',
  'fantasy',
  'inherit',
  'initial',
  'monospace',
  'revert',
  'sans-serif',
  'serif',
  'system-ui',
  'unset',
];

const GOOGLE_FONT_FAMILY_ALIASES: Record<string, string> = {
  BarlowSemiCondensed: 'Barlow Semi Condensed',
};

const getFontFamilies = (fontFamily: string) =>
  fontFamily
    .split(',')
    .map((family) => family.trim().replace(/^(['"])(.*)\1$/, '$2'))
    .filter((family) => family && !GENERIC_FONT_FAMILIES.includes(family.toLowerCase()));

const getPreviewFontFamily = (fontFamily: string) => {
  const families = fontFamily.split(',').map((family) => {
    const trimmedFamily = family.trim();
    const unquotedFamily = trimmedFamily.replace(/^(['"])(.*)\1$/, '$2');
    const resolvedFamily = GOOGLE_FONT_FAMILY_ALIASES[unquotedFamily];

    return resolvedFamily ? `"${resolvedFamily}"` : trimmedFamily;
  });

  return families.join(', ');
};

const isFontAvailable = (fontFamily: string) => {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  if (!context) {
    return false;
  }

  const testText = 'mmmmmmmmmmlli';
  const fallbackFamilies = ['monospace', 'sans-serif', 'serif'];

  return fallbackFamilies.some((fallbackFamily) => {
    context.font = `72px ${fallbackFamily}`;
    const fallbackWidth = context.measureText(testText).width;
    context.font = `72px "${fontFamily.replace(/"/g, '\\"')}", ${fallbackFamily}`;
    return context.measureText(testText).width !== fallbackWidth;
  });
};

const loadPreviewFont = (fontFamily: string) => {
  const googleFontFamily = GOOGLE_FONT_FAMILY_ALIASES[fontFamily] || fontFamily;
  const existingFontLink = Array.from(
    document.head.querySelectorAll<HTMLLinkElement>('link[data-contact-form-preview-font]'),
  ).some((link) => link.dataset.contactFormPreviewFont === googleFontFamily);

  if (existingFontLink || isFontAvailable(googleFontFamily)) {
    return;
  }

  const query = new URLSearchParams({
    family: googleFontFamily,
    display: 'swap',
  });
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?${query.toString()}`;
  link.dataset.contactFormPreviewFont = googleFontFamily;
  document.head.appendChild(link);
};

const usePreviewFonts = (fontFamily: string) => {
  useEffect(() => {
    getFontFamilies(fontFamily).forEach(loadPreviewFont);
  }, [fontFamily]);
};

const QuickPreview = ({ children, minHeight = 220 }: { children: ReactNode; minHeight?: number }) => (
  <Box p="lg" sx={{ minHeight }}>
    <Box sx={{ minHeight: minHeight - 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {children}
    </Box>
  </Box>
);

type PreviewCheckboxProps = {
  checkboxColor: string;
  fieldBackgroundColor: string;
  fieldBorderColor: string;
  linkColor: string;
};

const PreviewCheckbox = ({
  checkboxColor,
  fieldBackgroundColor,
  fieldBorderColor,
  linkColor,
}: PreviewCheckboxProps) => {
  const [checked, setChecked] = useState(true);

  return (
    <Box
      component="label"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        cursor: 'pointer',
        '&:focus-within .contact-form-preview-checkbox': {
          outline: `2px solid ${checkboxColor}`,
          outlineOffset: 2,
        },
      }}
    >
      <Box
        component="input"
        type="checkbox"
        aria-label="Datenschutzerklärung akzeptieren"
        checked={checked}
        onChange={(event: ChangeEvent<HTMLInputElement>) => setChecked(event.currentTarget.checked)}
        sx={{
          position: 'absolute',
          width: 1,
          height: 1,
          overflow: 'hidden',
          clip: 'rect(0 0 0 0)',
          clipPath: 'inset(50%)',
          whiteSpace: 'nowrap',
        }}
      />
      <Box
        className="contact-form-preview-checkbox"
        aria-hidden
        sx={{
          width: 16,
          height: 16,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: checked ? 0 : `2px solid ${fieldBorderColor}`,
          borderRadius: 3,
          background: checked ? checkboxColor : fieldBackgroundColor,
          color: '#ffffff',
          transition: 'background 150ms ease, border-color 150ms ease',
        }}
      >
        {checked && <IconCheck size={12} stroke={3} />}
      </Box>
      <MantineText inherit size="xs">
        Ich akzeptiere die{' '}
        <Box component="span" sx={{ color: linkColor, textDecoration: 'underline' }}>
          Datenschutzerklärung
        </Box>
      </MantineText>
    </Box>
  );
};

const ContactFormStyleOptions = () => {
  const { bookitupForm, bookitupFormStyles, updateFormStyles, updateForm } = useContext(BookitupFormContext);
  const { xs, md } = useContext(WindowContext);
  const [previewFontFamily, setPreviewFontFamily] = useState(bookitupFormStyles.fontFamily || '');
  const [debouncedPreviewFontFamily] = useDebouncedValue(previewFontFamily, 400);
  const gridColSpanSize = xs ? 12 : 6;
  const optionsColSpan = md ? 12 : 7;
  const previewColSpan = md ? 12 : 5;

  useEffect(() => {
    setPreviewFontFamily(bookitupFormStyles.fontFamily || '');
  }, [bookitupFormStyles.fontFamily]);

  usePreviewFonts(debouncedPreviewFontFamily);

  if (!bookitupForm) {
    return null;
  }

  const { actionButtonText, showStepperTopComponent } = bookitupForm;

  const {
    pageFormBackgroundColor,
    formBackgroundColor,
    backgroundImage,
    formTextColor,
    formFontSize,
    formLinkColor,
    formCheckboxColor,
    fieldBackgroundColor,
    fieldTextColor,
    fieldLabelTextWeight,
    fieldBorderColor,
    fieldBorderFocusColor,
    submitButtonBackgroundColor,
    submitButtonHoverColor,
    submitButtonTextColor,
    fieldAsteriskColor,
    fieldBorderRadius,
    fieldBorderWidth,
    submitButtonBorderRadius,
    submitButtonPosition,
    fontFamily,
    formBorderRadius,
    formPadding,
  } = bookitupFormStyles;

  return (
    <>
      <Autocomplete
        data={[
          '"Akshar", sans-serif',
          '"Antic", sans-serif',
          '"Cormorant Garamond", serif',
          '"Josefin Sans", sans-serif',
          '"Lato", sans-serif',
          '"Lora", serif',
          '"Montserrat", sans-serif',
          '"Quicksand", sans-serif',
          '"Roboto", sans-serif',
          '"Syne", sans-serif',
          '"Tenor Sans", sans-serif',
          '"Barlow Semi Condensed", sans-serif',
        ]}
        label="Schriftart"
        value={previewFontFamily}
        onChange={setPreviewFontFamily}
        onBlur={(e) => e.currentTarget.value !== fontFamily && updateFormStyles({ fontFamily: e.currentTarget.value })}
        icon={<IconLetterCase size={20} />}
        limit={50}
        maxDropdownHeight={200}
        sx={{ maxWidth: 600 }}
      />
      <MantineText color="dimmed" size="xs" mt={5} sx={{ maxWidth: 600 }}>
        Du kannst hier entweder eine von uns vorgegebene Schriftart auswählen oder eine eigene Schriftart eintragen, die
        bereits auf deiner Webseite verwendet wird. Mehrere Schriftarten können mit Komma getrennt aufgelistet werden,
        falls eine Schriftart nicht geladen werden kann.
      </MantineText>
      <NumberInput
        hideControls
        label="Schriftgröße"
        mt="sm"
        min={1}
        icon={<IconTextSize size={20} />}
        value={formFontSize ? Number(formFontSize) : 14}
        onBlur={(e) => e.currentTarget.value !== '' && updateFormStyles({ formFontSize: e.currentTarget.value })}
        rightSection={
          <ThemeText size="xs" weight="bolder">
            px
          </ThemeText>
        }
        sx={{ maxWidth: 600 }}
      />
      <Divider my="xl" label={<ThemeText size="md">Mini-Webseite</ThemeText>} />
      <ThemeText mb="xl">
        Diese Einstellung ist nur für dich relevant, falls du z.B. keine Webseite hast und das Kontaktformular direkt in
        deiner Instagram-Bio verlinken möchtest oder falls du eine Webseite bei Jimdo hast, bei der keine
        Code-Einbettung möglich ist.
      </ThemeText>
      <MantineColorPicker
        label="Hintergrundfarbe"
        color={pageFormBackgroundColor ?? undefined}
        onChange={(val) => updateFormStyles({ pageFormBackgroundColor: val })}
      />
      <Divider my="xl" label={<ThemeText size="md">Formular</ThemeText>} />
      <Grid gutter="xl" sx={{ alignItems: 'flex-start' }}>
        <Grid.Col span={optionsColSpan}>
          <TextInput
            mb="sm"
            label="Hintergrundbild"
            placeholder="http://domain.de/image-url.png"
            defaultValue={backgroundImage ?? undefined}
            onBlur={(e) => updateFormStyles({ backgroundImage: e.currentTarget.value })}
            icon={<IconPhoto size={20} />}
          />
          <NumberInput
            hideControls
            mb="sm"
            label="Rundung vom Formular"
            min={0}
            max={50}
            icon={<IconBorderRadius size={20} />}
            value={formBorderRadius ? Number(formBorderRadius) : 0}
            onBlur={(e) =>
              e.currentTarget.value !== '' && updateFormStyles({ formBorderRadius: e.currentTarget.value })
            }
            rightSection={
              <ThemeText size="xs" weight="bolder">
                px
              </ThemeText>
            }
          />
          <NumberInput
            hideControls
            label="Abstand vom Formularrand zu den Eingabefeldern"
            min={0}
            max={50}
            icon={<IconBorderRadius size={20} />}
            value={formPadding ? Number(formPadding) : 10}
            onBlur={(e) => e.currentTarget.value !== '' && updateFormStyles({ formPadding: e.currentTarget.value })}
            rightSection={
              <ThemeText size="xs" weight="bolder">
                px
              </ThemeText>
            }
          />
          <Checkbox
            label="Fortschrittsanzeige anzeigen"
            description="Zeigt oberhalb des Formulars eine Fortschrittsanzeige an, damit deine Kunden sehen, in welchem Schritt sie sich befinden."
            my="lg"
            checked={showStepperTopComponent ?? false}
            onChange={(e) => updateForm({ showStepperTopComponent: e.currentTarget.checked })}
          />
          <Grid mt="md" sx={{ alignItems: 'flex-start' }}>
            <Grid.Col span={gridColSpanSize}>
              <GradientPicker
                initialGradient={formBackgroundColor}
                label="Hintergrundfarbe"
                onChange={(val) => updateFormStyles({ formBackgroundColor: val })}
              />
            </Grid.Col>
            <Grid.Col span={gridColSpanSize}>
              <MantineColorPicker
                label="Textfarbe der Beschriftungen"
                color={formTextColor}
                onChange={(val) => updateFormStyles({ formTextColor: val })}
              />
            </Grid.Col>
            <Grid.Col span={gridColSpanSize}>
              <GradientPicker
                initialGradient={formCheckboxColor}
                label="Checkbox- & Dropdown-Auswahl"
                onChange={(val) => updateFormStyles({ formCheckboxColor: val })}
              />
            </Grid.Col>
            <Grid.Col span={gridColSpanSize}>
              <MantineColorPicker
                label="Linkfarbe"
                color={formLinkColor}
                onChange={(val) => updateFormStyles({ formLinkColor: val })}
              />
            </Grid.Col>
          </Grid>
        </Grid.Col>
        <Grid.Col
          span={previewColSpan}
          sx={(theme) => ({
            borderLeft: md
              ? 0
              : `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3]}`,
          })}
        >
          <QuickPreview minHeight={330}>
            <Box
              sx={{
                width: '100%',
                maxWidth: 390,
                padding: getPixelValue(formPadding, 10),
                borderRadius: getPixelValue(formBorderRadius, 0),
                background: formBackgroundColor || '#ffffff',
                backgroundImage: backgroundImage ? `url(${backgroundImage})` : undefined,
                backgroundPosition: 'center',
                backgroundSize: 'cover',
                color: formTextColor || '#212529',
                fontFamily: getPreviewFontFamily(previewFontFamily) || 'inherit',
                fontSize: getPixelValue(formFontSize, 14),
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
              }}
            >
              {showStepperTopComponent && (
                <Group spacing="xs" position="center" mb="md">
                  {[1, 2, 3].map((step) => (
                    <Box
                      key={step}
                      sx={{
                        width: 24,
                        height: 24,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '50%',
                        border: step === 1 ? 0 : '1px solid rgba(128, 128, 128, 0.45)',
                        background: step === 1 ? submitButtonBackgroundColor || '#228be6' : 'transparent',
                        color: step === 1 ? submitButtonTextColor || '#ffffff' : formTextColor || '#212529',
                        fontSize: 11,
                        fontWeight: 600,
                      }}
                    >
                      {step}
                    </Box>
                  ))}
                </Group>
              )}
              <MantineText inherit weight="bolder" mb={4}>
                Kontaktformular
              </MantineText>
              <MantineText inherit size="xs" mb="md" sx={{ opacity: 0.75 }}>
                Erzähl uns kurz von deinem Anliegen.
              </MantineText>
              <Box
                sx={{
                  height: 38,
                  borderRadius: getPixelValue(fieldBorderRadius, 4),
                  border: `${fieldBorderWidth || 'thin'} solid ${fieldBorderColor || '#ced4da'}`,
                  background: fieldBackgroundColor || '#ffffff',
                  marginBottom: 12,
                }}
              />
              <PreviewCheckbox
                checkboxColor={formCheckboxColor || '#228be6'}
                fieldBackgroundColor={fieldBackgroundColor || '#ffffff'}
                fieldBorderColor={fieldBorderColor || '#ced4da'}
                linkColor={formLinkColor || '#228be6'}
              />
            </Box>
          </QuickPreview>
        </Grid.Col>
      </Grid>
      <Divider my="xl" label={<ThemeText size="md">Feld</ThemeText>} />
      <Grid gutter="xl" sx={{ alignItems: 'flex-start' }}>
        <Grid.Col span={optionsColSpan}>
          <Autocomplete
            mb="sm"
            mt="xs"
            data={['lighter', 'normal', 'bold', 'bolder']}
            label="Schriftdicke der Feldbezeichnungen"
            defaultValue={fieldLabelTextWeight ?? 'bold'}
            onBlur={(e) => updateFormStyles({ fieldLabelTextWeight: e.currentTarget.value })}
          />
          <NumberInput
            hideControls
            label="Rundung der Eingabefelder"
            min={0}
            max={50}
            icon={<IconBorderRadius size={20} />}
            value={Number(fieldBorderRadius)}
            onBlur={(e) =>
              e.currentTarget.value !== '' && updateFormStyles({ fieldBorderRadius: e.currentTarget.value })
            }
            rightSection={
              <ThemeText size="xs" weight="bolder">
                px
              </ThemeText>
            }
          />
          <Autocomplete
            mt="xs"
            data={['thin', 'medium', 'thick']}
            label="Randdicke der Eingabefelder"
            defaultValue={fieldBorderWidth ?? undefined}
            onBlur={(e) => updateFormStyles({ fieldBorderWidth: e.currentTarget.value })}
          />
          <Grid mt="md" sx={{ alignItems: 'flex-start' }}>
            <Grid.Col span={gridColSpanSize}>
              <MantineColorPicker
                format="rgb"
                color={fieldBackgroundColor}
                label="Hintergrundfarbe"
                onChange={(val) => updateFormStyles({ fieldBackgroundColor: val })}
              />
            </Grid.Col>
            <Grid.Col span={gridColSpanSize}>
              <MantineColorPicker
                label="Textfarbe der Eingaben"
                color={fieldTextColor}
                onChange={(val) => updateFormStyles({ fieldTextColor: val })}
              />
            </Grid.Col>
            <Grid.Col span={gridColSpanSize}>
              <MantineColorPicker
                label="Rahmenfarbe"
                color={fieldBorderColor}
                onChange={(val) => updateFormStyles({ fieldBorderColor: val })}
              />
            </Grid.Col>
            <Grid.Col span={gridColSpanSize}>
              <MantineColorPicker
                label="Rahmenfarbe bei Fokus"
                color={fieldBorderFocusColor}
                onChange={(val) => updateFormStyles({ fieldBorderFocusColor: val })}
              />
            </Grid.Col>
            <Grid.Col span={gridColSpanSize}>
              <MantineColorPicker
                label="Sternchenfarbe für Pflichtfelder"
                color={fieldAsteriskColor}
                onChange={(val) => updateFormStyles({ fieldAsteriskColor: val })}
              />
            </Grid.Col>
          </Grid>
        </Grid.Col>
        <Grid.Col
          span={previewColSpan}
          sx={(theme) => ({
            borderLeft: md
              ? 0
              : `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3]}`,
          })}
        >
          <QuickPreview>
            <Box
              sx={{
                width: '100%',
                maxWidth: 390,
                padding: getPixelValue(formPadding, 10),
                borderRadius: getPixelValue(formBorderRadius, 0),
                background: formBackgroundColor || '#ffffff',
                fontFamily: getPreviewFontFamily(previewFontFamily) || 'inherit',
              }}
            >
              <MantineText
                component="label"
                htmlFor="contact-form-field-preview"
                size="sm"
                weight={fieldLabelTextWeight || 'bold'}
                mb={6}
                sx={{ color: formTextColor || 'inherit' }}
              >
                Vor- und Nachname{' '}
                <Box component="span" sx={{ color: fieldAsteriskColor || '#fa5252' }}>
                  *
                </Box>
              </MantineText>
              <Box
                component="input"
                id="contact-form-field-preview"
                defaultValue="Max Mustermann"
                readOnly
                sx={{
                  width: '100%',
                  height: 42,
                  padding: '0 12px',
                  border: `${fieldBorderWidth || 'thin'} solid ${fieldBorderColor || '#ced4da'}`,
                  borderRadius: getPixelValue(fieldBorderRadius, 4),
                  outline: 0,
                  background: fieldBackgroundColor || '#ffffff',
                  color: fieldTextColor || '#212529',
                  fontFamily: getPreviewFontFamily(previewFontFamily) || 'inherit',
                  fontSize: getPixelValue(formFontSize, 14),
                  transition: 'border-color 150ms ease, box-shadow 150ms ease',
                  '&:focus': {
                    borderColor: fieldBorderFocusColor || '#228be6',
                    boxShadow: `0 0 0 1px ${fieldBorderFocusColor || '#228be6'}`,
                  },
                }}
              />
              <MantineText color="dimmed" size="xs" mt="xs">
                Klicke in das Feld, um die Fokusfarbe zu sehen.
              </MantineText>
            </Box>
          </QuickPreview>
        </Grid.Col>
      </Grid>

      <Divider my="xl" label={<ThemeText size="md">Absendebutton</ThemeText>} />
      <Grid gutter="xl" sx={{ alignItems: 'flex-start' }}>
        <Grid.Col span={optionsColSpan}>
          <Stack>
            <Select
              label="Ausrichtung"
              icon={<IconAlignLeft size={20} />}
              value={submitButtonPosition}
              onChange={(val) => val && updateFormStyles({ submitButtonPosition: val })}
              data={DataS.buttonPositionOptions}
            />

            <NumberInput
              hideControls
              label="Rundung vom Absendebutton"
              min={0}
              max={50}
              icon={<IconBorderRadius size={20} />}
              value={Number(submitButtonBorderRadius)}
              onBlur={(e) =>
                e.currentTarget.value !== '' && updateFormStyles({ submitButtonBorderRadius: e.currentTarget.value })
              }
              rightSection={
                <ThemeText size="xs" weight="bolder">
                  px
                </ThemeText>
              }
            />
          </Stack>
          <Grid mt="md" sx={{ alignItems: 'flex-start' }}>
            <Grid.Col span={gridColSpanSize}>
              <GradientPicker
                initialGradient={submitButtonBackgroundColor}
                label="Hintergrundfarbe"
                onChange={(val) => updateFormStyles({ submitButtonBackgroundColor: val })}
              />
            </Grid.Col>
            <Grid.Col span={gridColSpanSize}>
              <MantineColorPicker
                label="Textfarbe"
                color={submitButtonTextColor}
                onChange={(val) => updateFormStyles({ submitButtonTextColor: val })}
              />
            </Grid.Col>
            <Grid.Col span={gridColSpanSize}>
              <MantineColorPicker
                label="Hoverfarbe"
                color={submitButtonHoverColor}
                onChange={(val) => updateFormStyles({ submitButtonHoverColor: val })}
              />
            </Grid.Col>
          </Grid>
        </Grid.Col>
        <Grid.Col
          span={previewColSpan}
          sx={(theme) => ({
            borderLeft: md
              ? 0
              : `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3]}`,
          })}
        >
          <QuickPreview>
            <Box
              sx={{
                width: '100%',
                maxWidth: 390,
                display: 'flex',
                padding: getPixelValue(formPadding, 10),
                borderRadius: getPixelValue(formBorderRadius, 0),
                background: formBackgroundColor || '#ffffff',
                justifyContent:
                  submitButtonPosition === 'right'
                    ? 'flex-end'
                    : submitButtonPosition === 'center'
                    ? 'center'
                    : 'flex-start',
              }}
            >
              <Box
                component="button"
                type="button"
                sx={{
                  minWidth: 150,
                  padding: '10px 18px',
                  border: 0,
                  borderRadius: getPixelValue(submitButtonBorderRadius, 4),
                  background: submitButtonBackgroundColor || '#228be6',
                  color: submitButtonTextColor || '#ffffff',
                  cursor: 'pointer',
                  fontFamily: getPreviewFontFamily(previewFontFamily) || 'inherit',
                  fontSize: getPixelValue(formFontSize, 14),
                  fontWeight: 600,
                  transition: 'background 150ms ease, transform 150ms ease',
                  '&:hover': {
                    background: submitButtonHoverColor || '#1c7ed6',
                    transform: 'translateY(-1px)',
                  },
                }}
              >
                {actionButtonText || 'Anfrage senden'}
              </Box>
            </Box>
          </QuickPreview>
        </Grid.Col>
      </Grid>
    </>
  );
};

export default ContactFormStyleOptions;
